/**
 * Image utilities for thumbnail generation and URL management
 * Thumbnails are generated client-side and uploaded alongside originals
 * This reduces egress costs by serving smaller images in grid views
 */

// Thumbnail configuration
export const THUMBNAIL_CONFIG = {
  maxDimension: 200,  // Max width/height in pixels
  quality: 0.75,      // JPEG quality (0-1)
  suffix: '_thumb',   // Suffix added to thumbnail filename
};

/**
 * Generate a thumbnail from an image file
 * @param {File} file - Original image file
 * @param {number} maxDimension - Max width/height (default: 200)
 * @param {number} quality - JPEG quality 0-1 (default: 0.75)
 * @returns {Promise<File>} Thumbnail file
 */
export function generateThumbnail(file, maxDimension = THUMBNAIL_CONFIG.maxDimension, quality = THUMBNAIL_CONFIG.quality) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create thumbnail filename
              const originalName = file.name;
              const lastDot = originalName.lastIndexOf('.');
              const baseName = lastDot > 0 ? originalName.substring(0, lastDot) : originalName;
              const thumbName = `${baseName}${THUMBNAIL_CONFIG.suffix}.jpg`;

              const thumbnailFile = new File([blob], thumbName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(thumbnailFile);
            } else {
              reject(new Error('Failed to generate thumbnail'));
            }
          },
          'image/jpeg',
          quality
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for thumbnail generation'));
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate thumbnail storage path from original path
 * @param {string} originalPath - Original file storage path
 * @returns {string} Thumbnail storage path
 */
export function getThumbnailPath(originalPath) {
  if (!originalPath) return null;

  const lastDot = originalPath.lastIndexOf('.');
  if (lastDot === -1) {
    return `${originalPath}${THUMBNAIL_CONFIG.suffix}`;
  }

  const basePath = originalPath.substring(0, lastDot);
  // Always use .jpg for thumbnails
  return `${basePath}${THUMBNAIL_CONFIG.suffix}.jpg`;
}

/**
 * Check if a path is a thumbnail path
 * @param {string} path - Storage path to check
 * @returns {boolean} True if path is a thumbnail
 */
export function isThumbnailPath(path) {
  if (!path) return false;
  return path.includes(THUMBNAIL_CONFIG.suffix);
}

/**
 * Get original path from thumbnail path
 * @param {string} thumbnailPath - Thumbnail storage path
 * @returns {string} Original storage path
 */
export function getOriginalPath(thumbnailPath) {
  if (!thumbnailPath) return null;

  // Remove _thumb suffix and restore original extension
  // This is a best-effort since we don't know the original extension
  return thumbnailPath.replace(`${THUMBNAIL_CONFIG.suffix}.jpg`, '.jpg');
}

export default {
  THUMBNAIL_CONFIG,
  generateThumbnail,
  getThumbnailPath,
  isThumbnailPath,
  getOriginalPath,
};
